import React, { useMemo } from 'react';
import { Activity } from 'lucide-react';
import { TimeEntry } from '../../types/database';
import { cn } from '../../lib/utils';
import { Card, CardBody, CardHeader } from '../ui/card';

interface HeatmapWidgetProps {
  timeEntries: TimeEntry[];
  year: number;
  month: number;
}

interface HeatmapDay {
  date: string;
  day: number;
  level: number;
  hours: number;
}

const WEEKDAY_LABELS = ['Mon', '', 'Wed', '', 'Fri', '', 'Sun'];

const LEVEL_CLASSES = [
  'bg-white/[0.045]',
  'bg-brand/25',
  'bg-brand/45',
  'bg-brand/70',
  'bg-brand shadow-[0_0_10px_rgb(155_109_255/0.45)]',
];

const pad = (n: number) => n.toString().padStart(2, '0');

/** Any logged time gets at least level 1; 8h+ days hit the top level. */
const getLevel = (hours: number): number => (hours <= 0 ? 0 : Math.min(4, Math.ceil(hours / 2)));

export const HeatmapWidget: React.FC<HeatmapWidgetProps> = ({ timeEntries, year, month }) => {
  const { weeks, activeDays, totalHours } = useMemo(() => {
    const entriesMap = new Map<string, TimeEntry>();
    timeEntries.forEach(entry => entriesMap.set(entry.date, entry));

    const daysInMonth = new Date(year, month, 0).getDate();
    const firstWeekday = new Date(year, month - 1, 1).getDay();
    const leadingBlanks = firstWeekday === 0 ? 6 : firstWeekday - 1;

    // Build the month as a flat Monday-first grid, then slice into week columns.
    const cells: (HeatmapDay | null)[] = Array.from({ length: leadingBlanks }, () => null);
    let active = 0;
    let total = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const date = `${year}-${pad(month)}-${pad(day)}`;
      const hours = entriesMap.get(date)?.totalHours || 0;
      if (hours > 0) active++;
      total += hours;
      cells.push({ date, day, hours, level: getLevel(hours) });
    }
    while (cells.length % 7 !== 0) cells.push(null);

    const columns: (HeatmapDay | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) columns.push(cells.slice(i, i + 7));

    return { weeks: columns, activeDays: active, totalHours: total };
  }, [timeEntries, year, month]);

  return (
    <Card>
      <CardHeader
        icon={<Activity />}
        title="Activity"
        description={`${activeDays} active ${activeDays === 1 ? 'day' : 'days'} · ${totalHours.toFixed(1)}h total`}
      />
      <CardBody>
        <div
          className="grid justify-center gap-1.5"
          style={{ gridTemplateColumns: `auto repeat(${weeks.length}, minmax(0, 2.25rem))` }}
        >
          {/* Weekday labels column */}
          <div className="grid grid-rows-7 gap-1.5 pr-1">
            {WEEKDAY_LABELS.map((label, i) => (
              <span key={i} className="flex items-center text-[11px] text-fg-subtle">
                {label}
              </span>
            ))}
          </div>

          {weeks.map((week, w) => (
            <div key={w} className="grid grid-rows-7 gap-1.5">
              {week.map((day, d) =>
                day ? (
                  <div
                    key={day.date}
                    title={`${day.date}: ${day.hours.toFixed(1)}h`}
                    className={cn(
                      'aspect-square rounded-[5px] transition-transform duration-150 hover:scale-110',
                      LEVEL_CLASSES[day.level]
                    )}
                  />
                ) : (
                  <div key={`empty-${w}-${d}`} aria-hidden className="aspect-square" />
                )
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-end gap-1.5 text-[11px] text-fg-subtle">
          <span>Less</span>
          {LEVEL_CLASSES.map((cls, level) => (
            <span key={level} className={cn('size-3 rounded-[3px]', cls)} />
          ))}
          <span>More</span>
        </div>
      </CardBody>
    </Card>
  );
};
